import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { TableRow, Typography, MenuItem, ListItemText } from '@mui/material';

import { TableBodyCell } from '../table-cells/TableCells';
import TextField from '../text-field/TextField';
import MultiSelect from '../multi-select/MultiSelect';
import Checkbox from '../checkbox/Checkbox';
import TableRowButtons from './TableRowButtons';
import DeleteLocalDomainDialog from './DeleteLocalDomainDialog';

import { validateLocalDomainName } from '../../utils/validateLocalDomainName';

import {
  useGetAuthenticationQuery,
  useGetDomainControllersQuery,
  useEditLocalDomainMutation,
} from '../../services/ADapi';
import { notificationSuccess } from '../../store/notificationReducer';

/**
 *
 * @param {string | number} id
 * @param {string} name
 * @param {number[]} domainControllerIds
 * @returns
 */

const LocalDomainTableRow = ({ id, name, controllerIds }) => {
  const dispatch = useDispatch();
  const [isEditable, setEditableState] = useState();
  const [isFormValid, setFormState] = useState(false);
  const [errors, setErrors] = useState({});
  const [domainControllerIds, setDomainControllerIds] = useState([]);
  const [localDomainName, setLocalDomainName] = useState(name);
  const [isDialogOpen, setDialogState] = useState(false);

  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: controllers } = useGetDomainControllersQuery(user_id);
  const [editLocalDomain] = useEditLocalDomainMutation();

  useEffect(() => {
    const selectedDomainControllers = controllers.filter((controller) =>
      controllerIds.includes(controller.id)
    );

    setDomainControllerIds(selectedDomainControllers);
  }, [controllerIds, controllers]);

  useEffect(() => {
    const localDomainNameValid = validateLocalDomainName(localDomainName);

    setFormState(localDomainNameValid);
  }, [localDomainName]);

  const switchToEditMode = () => {
    setEditableState(true);
  };

  const declineEditMode = () => {
    setEditableState(false);
  };

  const localDomainNameHandler = (e) => {
    const errorsCopy = { ...errors };
    delete errorsCopy.domain;
    setErrors(errorsCopy);
    setLocalDomainName(e.target.value);
  };

  const handleChange = (e) => {
    const value = e.target.value;

    setDomainControllerIds(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const editLocalDomainHandler = async () => {
    const ids = domainControllerIds.map((c) => c.id);

    try {
      await editLocalDomain({
        user_id,
        localDomain: {
          id,
          domain: localDomainName,
          controller_ids: ids,
        },
      }).unwrap();

      dispatch(
        notificationSuccess({
          isNotification: true,
          message: 'Changes saved',
        })
      );
      setErrors({});
      setEditableState(false);
    } catch (errors) {
      setErrors(errors);
    }
  };

  const dialogHandler = () => {
    setDialogState((prevState) => !prevState);
  };

  return (
    <>
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': { border: 0 },
          verticalAlign: isEditable ? 'top' : 'middle',
        }}
      >
        <TableBodyCell scope="row">
          {isEditable ? (
            <TextField
              value={localDomainName}
              placeholder="Name"
              onChange={localDomainNameHandler}
            />
          ) : (
            name
          )}
          {errors.domain && isEditable ? (
            <Typography variant="body2" color="#e08d8d">
              {errors.domain}
            </Typography>
          ) : null}
        </TableBodyCell>
        <TableBodyCell align="left">
          {isEditable ? (
            <MultiSelect
              value={domainControllerIds}
              multiple
              onChange={handleChange}
              renderValue={(selected) =>
                selected
                  .map(
                    (val) =>
                      // TODO: This ? mess is due to load entities race (we definitely need to wait until all entities are loaded)
                      controllers.find((c) => c?.name === val?.name)?.name
                  )
                  .join(', ')
              }
              sx={{ maxWidth: '360px' }}
            >
              {controllers.map((controller) => (
                <MenuItem
                  key={controller.id}
                  value={controller}
                  sx={{
                    gap: 2,
                  }}
                >
                  <Checkbox
                    checked={domainControllerIds?.some(
                      (c) => c?.id === controller.id
                    )}
                  />
                  <ListItemText primary={controller.name} />
                </MenuItem>
              ))}
            </MultiSelect>
          ) : (
            controllerIds.length
          )}

          {errors.controller_ids && isEditable ? (
            <Typography variant="body2" color="#e08d8d">
              {errors.controller_ids}
            </Typography>
          ) : null}
        </TableBodyCell>
        <TableRowButtons
          isEditable={isEditable}
          onDialogOpen={dialogHandler}
          switchToEditMode={switchToEditMode}
          declineEditMode={declineEditMode}
          onSave={editLocalDomainHandler}
          isSaveDisabled={!isFormValid}
        />
      </TableRow>
      {isDialogOpen ? (
        <DeleteLocalDomainDialog localDomainId={id} onClose={dialogHandler} />
      ) : null}
    </>
  );
};

export default LocalDomainTableRow;
